// The proposal state machine, shared shape with the Edge Function.
//
// The UI renders exactly allowedActions(status) — never a disabled superset —
// so a button that exists always does something. Mirrors
// resources/businesses/decision.ts, which established this pattern here.

import type { ProposalStatus, CmProposal, Channel, CmSettings } from './types'
import type { Tone } from '../../theme/listkit'

export type DecisionAction =
  | 'approve' | 'approve_edited' | 'reject' | 'snooze' | 'send_now' | 'cancel'

export function allowedActions(status: ProposalStatus): DecisionAction[] {
  switch (status) {
    case 'pending_review':
      return ['approve', 'approve_edited', 'reject', 'snooze', 'send_now']
    case 'approved':
    case 'scheduled':
      return ['cancel', 'send_now']
    // In flight: the dispatcher has already claimed the delivery rows, so
    // nothing here is safe to press.
    case 'sending':
      return []
    case 'failed':
      return ['reject', 'send_now']
    case 'sent':
    case 'partially_sent':
    case 'rejected':
    case 'expired':
    case 'superseded':
      return []
    default:
      return []
  }
}

/** A rejection without a reason is a rejection nobody can learn from. */
export function noteRequired(a: DecisionAction): boolean {
  return a === 'reject'
}

export function actionLabel(a: DecisionAction): string {
  switch (a) {
    case 'approve': return 'Approve'
    case 'approve_edited': return 'Edit & approve'
    case 'reject': return 'Reject'
    case 'snooze': return 'Snooze'
    case 'send_now': return 'Send now'
    case 'cancel': return 'Recall'
  }
}

export function statusLabel(s: ProposalStatus): string {
  switch (s) {
    case 'pending_review': return 'Needs you'
    case 'approved': return 'Approved'
    case 'scheduled': return 'Scheduled'
    case 'sending': return 'Sending'
    case 'sent': return 'Sent'
    case 'partially_sent': return 'Partly sent'
    case 'failed': return 'Failed'
    case 'rejected': return 'Rejected'
    case 'expired': return 'Expired'
    case 'superseded': return 'Recalled'
    default: return s
  }
}

export function statusTone(s: ProposalStatus): Tone {
  switch (s) {
    case 'pending_review': return 'gold'
    case 'approved':
    case 'scheduled': return 'jaguar'
    case 'sent': return 'green'
    case 'failed':
    case 'partially_sent': return 'red'
    default: return 'neutral'
  }
}

export function riskLabel(tier: number): string {
  switch (tier) {
    case 0: return 'Low risk'
    case 1: return 'Targeted'
    case 2: return 'Broadcast / free text'
    default: return 'Sensitive'
  }
}

export function riskTone(tier: number): Tone {
  if (tier === 0) return 'green'
  if (tier === 1) return 'jaguar'
  if (tier === 2) return 'gold'
  return 'red'
}

export function channelLabel(c: Channel): string {
  switch (c) {
    case 'inapp': return 'In-app'
    case 'push': return 'Push'
    case 'feed': return 'Feed'
  }
}

/**
 * The copy a member will actually receive: the agent's original, with any
 * admin edit applied on top.
 */
export function effectiveCopy(p: CmProposal): { title: string; body: string; feedText: string } {
  const e = (p.edited_payload ?? {}) as Record<string, unknown>
  const pick = (k: 'title' | 'body' | 'feed_text') =>
    typeof e[k] === 'string' ? (e[k] as string) : ((p[k] as string | null) ?? '')
  return { title: pick('title'), body: pick('body'), feedText: pick('feed_text') }
}

export const EDITABLE_FIELDS = ['title', 'body', 'feed_text'] as const
export type EditableField = typeof EDITABLE_FIELDS[number]

/**
 * Merge an admin's edits onto the agent's payload.
 *
 * Starts from a spread of the original, so any key the UI has no editor for
 * survives BY CONSTRUCTION rather than by anyone remembering to preserve it.
 * This is the client half of the defence against the trap recorded in
 * notification_rules/RuleFormFields.tsx, where rc-field-form's onFinish emitted
 * only registered name paths and silently deleted the rest of a jsonb column on
 * every save. The other halves: the wire carries a PATCH of named fields rather
 * than a whole payload, and the server merges against its own allowlist.
 */
export function applyEdits<T extends Record<string, unknown>>(
  original: T,
  edits: Partial<Record<EditableField, string>> | null | undefined,
): T {
  const out = { ...original }
  if (!edits) return out
  for (const f of EDITABLE_FIELDS) {
    const v = edits[f]
    if (typeof v === 'string') (out as Record<string, unknown>)[f] = v
  }
  return out
}

/**
 * An edit that changes the copy makes it free text, and free text is tier 2 —
 * which Tiered mode will not auto-send. Without this an admin could unknowingly
 * launder a rewritten message through an allowlisted kind.
 */
export function riskTierAfterEdit(
  originalTier: number,
  original: Record<string, unknown>,
  edits: Partial<Record<EditableField, string>> | null | undefined,
): number {
  if (!edits) return originalTier
  const changed = EDITABLE_FIELDS.some(
    (f) => typeof edits[f] === 'string' && edits[f] !== original[f],
  )
  return changed ? Math.max(originalTier, 2) : originalTier
}

/**
 * True when an approved proposal is waiting only because every channel it uses
 * is switched off. The dispatcher HOLDS these rather than discarding them, so
 * the Inbox has to say so — otherwise "Approved" sits there with no reason, and
 * the owner cannot tell a held message from a stuck one.
 */
export function heldByChannel(
  p: Pick<CmProposal, 'status' | 'channels'>,
  s: Pick<CmSettings, 'channel_inapp_enabled' | 'channel_push_enabled' | 'channel_feed_enabled'> | null,
): boolean {
  if (!s) return false
  if (p.status !== 'approved' && p.status !== 'scheduled') return false
  const on: Record<Channel, boolean> = {
    inapp: s.channel_inapp_enabled, push: s.channel_push_enabled, feed: s.channel_feed_enabled,
  }
  return p.channels.length > 0 && p.channels.every((c) => !on[c])
}

/**
 * The actions a card may offer in the CURRENT MODE — a narrowing of
 * allowedActions(status), never a widening.
 *
 * In shadow and off nothing can be sent, so every action whose only purpose is
 * to send is withheld: offering Approve under a banner that says "nothing below
 * can be sent" was a contradiction, and an approval recorded in shadow sits
 * inert and then goes out by surprise the moment the mode is widened. What
 * stays is what still means something: Reject (a standing instruction to the
 * agent) and Recall (withdrawing an approval made earlier). In shadow the card
 * offers good / not-good marks instead — that is what the mode is for.
 */
export function actionsForMode(status: ProposalStatus, mode: string): DecisionAction[] {
  const all = allowedActions(status)
  if (mode === 'shadow' || mode === 'off') {
    return all.filter((a) => a === 'reject' || a === 'cancel')
  }
  return all
}

/** What public.cm_proposal_preview(id) says about reach (subset). */
export interface ReachPreview {
  audience_count: number
  reachable_count: number
  outside_rollout: number
  restricted: number
  first_recipient_first_name: string | null
}

/**
 * The sentence the confirmation dialog shows about who will receive this.
 *
 * Uses the SERVER's reachable count — the audience after the rollout allowlist
 * and member restrictions, which is what the dispatcher will actually deliver
 * to. The earlier version counted the proposal's userIds in the browser, and a
 * task addressed to "admins" has none, so it announced "0 members" for a note
 * that was in fact going to the owner.
 */
export function reachSentence(
  p: Pick<CmProposal, 'kind' | 'audience' | 'channels'>,
  pv: ReachPreview | null | undefined,
): string {
  const over = p.channels.map(channelLabel).join(' + ')
  if (p.audience?.type === 'generic') return `This posts to the community feed (${over}).`
  if (!pv) return `Who this reaches is still being checked — it goes over ${over}.`

  const n = pv.reachable_count
  const who = p.kind === 'admin_task' ? 'admin' : 'member'
  const name = n === 1 && pv.first_recipient_first_name ? ` (${pv.first_recipient_first_name})` : ''

  if (n === 0) {
    const why = pv.outside_rollout > 0
      ? 'none of its recipients are on the rollout allowlist'
      : pv.restricted > 0 ? 'its recipients are restricted' : 'it has no recipients'
    return `This will reach nobody: ${why}.`
  }
  const held = pv.audience_count - n
  return `This will reach ${n} ${who}${n === 1 ? '' : 's'}${name} over ${over}` +
    (held > 0 ? `, and not ${held} other${held === 1 ? '' : 's'} who ${held === 1 ? 'is' : 'are'} not on the rollout allowlist or restricted.` : '.')
}
